/**
 * Legacy pricing module. **New code should import `../payments/*` instead.**
 *
 * The `VITE_STRIPE_*` price IDs below are build-time client config, which is why
 * adding a price today means a config key, repo wiring and a client deploy. The
 * replacement is the declared Stripe catalog, resolved server-side by
 * `lookup_key` (see `@listening/backend/stripe/price-resolver`); this file goes
 * away once no caller remains.
 */
import {
  PRICING_TEST_V1_ARMS,
  type PricingTestAnnualTierUsd,
  type PricingTestV1Variant,
} from '@listening/api-contract/pricing-experiment';

type PaymentPeriod = 'yearly' | 'monthly';

export type PaymentPlan = {
  amount: number;
  priceId: string;
  period: PaymentPeriod;
};

export function getDefaultPaymentPlans(env: {
  VITE_STRIPE_YEARLY_99_ID: string;
  VITE_STRIPE_YEARLY_39_ID: string;
  VITE_STRIPE_MONTHLY_12_99_ID: string;
}) {
  const monthly = {
    amount: 12.99,
    priceId: env.VITE_STRIPE_MONTHLY_12_99_ID,
    period: 'monthly',
  } as const;

  const yearly = {
    amount: 39,
    priceId: env.VITE_STRIPE_YEARLY_39_ID,
    period: 'yearly',
  } as const;

  return {
    monthly,
    yearly,
  } as const;
}

/**
 * The two plans quoted to a pricing-test-v1 arm. Only the annual tier varies;
 * every arm shares the $12.99 monthly plan, and `control` resolves to the same
 * price as `getDefaultPaymentPlans`. The trial dimension lives on the arm
 * contract, not here.
 */
export function getPricingTestPaymentPlans(
  env: {
    VITE_STRIPE_YEARLY_39_ID: string;
    VITE_STRIPE_YEARLY_59_ID: string;
    VITE_STRIPE_YEARLY_79_ID: string;
    VITE_STRIPE_MONTHLY_12_99_ID: string;
  },
  variant: PricingTestV1Variant,
) {
  const monthly = {
    amount: 12.99,
    priceId: env.VITE_STRIPE_MONTHLY_12_99_ID,
    period: 'monthly',
  } as const;

  const annualTiers: Record<PricingTestAnnualTierUsd, PaymentPlan> = {
    39: {
      amount: 39,
      priceId: env.VITE_STRIPE_YEARLY_39_ID,
      period: 'yearly',
    },
    59: {
      amount: 59,
      priceId: env.VITE_STRIPE_YEARLY_59_ID,
      period: 'yearly',
    },
    79: {
      amount: 79,
      priceId: env.VITE_STRIPE_YEARLY_79_ID,
      period: 'yearly',
    },
  };

  return {
    monthly,
    yearly: annualTiers[PRICING_TEST_V1_ARMS[variant].annualTierUsd],
  } as const;
}

export const trialDays = 3;
